import { useEffect, useState } from "react";
import { DatePicker } from "components/DatePicker";
import { LuX } from "react-icons/lu";
import { appConstants } from "utils/appConstants";
import { showResMsg } from "utils/generic";
import { showDraftSavedMessage } from "components/InlineFeedback/DraftSaveConfirmation";
import { submitInvoicePayment } from "utils/invoiceAPI";
import InvoiceSelect from "components/FormControls/ThemedSelect";

const PAYMENT_MODES = [
  "Cash",
  "Paypal",
  "Card",
  "Cheque",
  "Bank Transfer",
  "Wire Transfer",
];

const localDateValue = () => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
};

const toLocalDateValue = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

function RecordInvoicePaymentDialog({
  open,
  onClose,
  onRecorded,
  invoice,
  outstanding,
  totalPaid = 0,
  currency,
  dispatch,
}) {
  const [form, setForm] = useState({
    paymentType: "Full Payment",
    paymentAmount: "",
    paymentDate: localDateValue(),
    paymentMode: "",
    paymentTransactionID: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      paymentType: "Full Payment",
      paymentAmount: Number(outstanding || 0).toFixed(2),
      paymentDate: localDateValue(),
      paymentMode: "",
      paymentTransactionID: "",
    });
    setErrors({});
  }, [open, outstanding]);

  if (!open) return null;

  const amount = Number(form.paymentAmount || 0);
  const remaining = Math.max(0, Number(outstanding || 0) - amount);
  const invoiceTotal = Number(
    invoice?.finalAmount || invoice?.totalAmount || 0
  );
  const transactionRequired = !["Cash", "Cheque"].includes(form.paymentMode);
  const parsedDueDate = invoice?.dueDate ? new Date(invoice.dueDate) : null;
  const dueDate =
    parsedDueDate && !Number.isNaN(parsedDueDate.getTime())
      ? new Intl.DateTimeFormat("en-AU", {
          day: "numeric",
          month: "short",
          year: "numeric",
        }).format(parsedDueDate)
      : "—";

  const setField = (name, value) => {
    setForm((current) => {
      const next = { ...current, [name]: value };
      if (name === "paymentType") {
        next.paymentAmount =
          value === "Full Payment" ? Number(outstanding || 0).toFixed(2) : "";
      }
      return next;
    });
    setErrors((current) => ({ ...current, [name]: "" }));
  };

  const validate = () => {
    const next = {};
    if (!amount || amount <= 0)
      next.paymentAmount = "Enter an amount greater than zero.";
    if (amount > Number(outstanding || 0))
      next.paymentAmount = "Payment cannot exceed the outstanding balance.";
    if (!form.paymentDate) next.paymentDate = "Choose a payment date.";
    if (!form.paymentMode) next.paymentMode = "Choose a payment method.";
    if (transactionRequired && !form.paymentTransactionID.trim())
      next.paymentTransactionID = "Enter the transaction ID.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = () => {
    if (submitting || !validate()) return;
    const loginDetails = JSON.parse(
      sessionStorage.getItem("LOGIN_DETAILS") || "{}"
    );
    setSubmitting(true);
    submitInvoicePayment(
      {
        userID: loginDetails?._id,
        invoiceID: invoice?._id || invoice?.invoiceID,
        contactName: invoice?.contactName || "",
        paymentType: form.paymentType,
        paymentAmount: amount,
        paymentDate: form.paymentDate,
        paymentMode: form.paymentMode,
        paymentTransactionID: form.paymentTransactionID.trim(),
        sendEmailFlag: false,
        signatureKey: appConstants.SIGNATURE_KEY,
      },
      async (result) => {
        setSubmitting(false);
        if (result?.Result_Status) {
          showDraftSavedMessage("Payment recorded successfully");
          await onRecorded?.();
          onClose();
        } else {
          showResMsg(result?.Result_Message || "Unable to record payment.", 2000, false);
        }
      },
      dispatch
    );
  };

  return (
    <div
      className="record-payment-backdrop"
      role="presentation"
      onMouseDown={(event) =>
        event.target === event.currentTarget && !submitting && onClose()
      }
    >
      <section
        className="record-payment-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="record-payment-title"
      >
        <header className="record-payment-header">
          <h2 id="record-payment-title">Record payment</h2>
          <button
            type="button"
            aria-label="Close record payment"
            onClick={onClose}
            disabled={submitting}
          >
            <LuX aria-hidden="true" />
            <span>Close</span>
          </button>
        </header>

        <div className="record-payment-content">
          <p className="record-payment-invoice">
            {invoice?.invoiceNumber} · {invoice?.contactName || "Customer"} ·
            Due {dueDate}
          </p>
          <span className="record-payment-status">
            Awaiting payment{totalPaid > 0 ? " · Part-paid" : ""}
          </span>
          <div className="record-payment-balance">
            <span>Invoice total</span>
            <strong>
              {currency} {invoiceTotal.toFixed(2)}
            </strong>
          </div>
          <div className="record-payment-balance">
            <span>Payments already received</span>
            <strong>
              {currency} {Number(totalPaid || 0).toFixed(2)}
            </strong>
          </div>
          <div className="record-payment-balance">
            <span>Applied credits</span>
            <strong>{currency} 0.00</strong>
          </div>
          <div className="record-payment-balance">
            <span>Current remaining balance</span>
            <strong>
              {currency} {Number(outstanding || 0).toFixed(2)}
            </strong>
          </div>

          <div className="record-payment-field">
            <label htmlFor="record-payment-type">Payment type</label>
            <InvoiceSelect
              id="record-payment-type"
              aria-label="Payment type"
              value={form.paymentType}
              onChange={(event) => setField("paymentType", event.target.value)}
              disabled={submitting}
            >
              <option value="Partial Payment">Partial Payment</option>
              <option value="Full Payment">Full Payment</option>
            </InvoiceSelect>
          </div>

          <label>
            Amount · {currency} <em>*</em>
            <input
              type="number"
              min="0.01"
              max={outstanding}
              step="0.01"
              value={form.paymentAmount}
              onChange={(event) =>
                setField("paymentAmount", event.target.value)
              }
              readOnly={form.paymentType === "Full Payment"}
              disabled={submitting}
            />
            {errors.paymentAmount && (
              <small className="record-payment-error">
                {errors.paymentAmount}
              </small>
            )}
          </label>

          <div className="record-payment-field">
            <span className="record-payment-field-label">
              Payment date <em>*</em>
            </span>
            <DatePicker
              id="record-payment-date"
              selected={form.paymentDate}
              onChange={(date) =>
                setField("paymentDate", toLocalDateValue(date))
              }
              dateFormat="d MMM yyyy"
              placeholder="Select payment date"
              disabled={submitting}
              error={Boolean(errors.paymentDate)}
              aria-label="Payment date"
            />
            {errors.paymentDate && (
              <small className="record-payment-error">
                {errors.paymentDate}
              </small>
            )}
          </div>

          <div className="record-payment-field">
            <label htmlFor="record-payment-method">
              Method <em>*</em>
            </label>
            <InvoiceSelect
              id="record-payment-method"
              aria-label="Payment method"
              aria-invalid={Boolean(errors.paymentMode)}
              value={form.paymentMode}
              onChange={(event) => setField("paymentMode", event.target.value)}
              displayValue={form.paymentMode || "Select payment method"}
              disabled={submitting}
            >
              <option value="">Select payment method</option>
              {PAYMENT_MODES.map((mode) => (
                <option key={mode} value={mode}>
                  {mode}
                </option>
              ))}
            </InvoiceSelect>
            {errors.paymentMode && (
              <small className="record-payment-error">
                {errors.paymentMode}
              </small>
            )}
          </div>

          <label>
            Transaction ID / reference {transactionRequired ? "" : "(optional)"}
            <input
              type="text"
              maxLength={50}
              placeholder="Enter payment reference"
              value={form.paymentTransactionID}
              onChange={(event) =>
                setField("paymentTransactionID", event.target.value)
              }
              disabled={submitting}
            />
            {errors.paymentTransactionID && (
              <small className="record-payment-error">
                {errors.paymentTransactionID}
              </small>
            )}
          </label>

          <div className="record-payment-projection">
            <strong>
              Projected balance after payment: {currency} {remaining.toFixed(2)}
            </strong>
            <span>
              {currency} {Number(outstanding || 0).toFixed(2)} − {currency}{" "}
              {amount.toFixed(2)}. Preview only — not yet recorded. This records
              a payment received, not an online charge.
            </span>
          </div>
          <p className="record-payment-help">
            Invoice total and due date stay unchanged. The balance updates only
            after recording succeeds.
          </p>
        </div>
        <footer className="record-payment-actions">
          <button type="button" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            className="primary"
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Recording payment…" : "Record payment"}
          </button>
        </footer>
      </section>
    </div>
  );
}

export default RecordInvoicePaymentDialog;
